/**
 * Brand brief editor section on the Theme tab (#197 + #230).
 *
 * Renders an MUI `Accordion` with the four `brandJsonV1` fields:
 *   - `tagline` — single-line, ≤140 (with character counter).
 *   - `tone` — single-line, ≤80 (with helper hint "warm, expert, concise").
 *   - `audience` — multiline, ≤200.
 *   - `voiceNotes` — multiline, ≤1000.
 *
 * Form choices:
 *   - The accordion is expanded by default when the site has no brand brief
 *     on file yet (`brandJson` is null OR the empty envelope) — new sites
 *     see the form immediately. It collapses by default once the operator
 *     has saved a brief, so the editor focuses on theme by default on
 *     subsequent visits.
 *   - Field-level character counters surface as `helperText` so the
 *     operator sees the remaining budget as they type. Zod's max-length
 *     errors take precedence over the counter when an oversize value is
 *     entered (the form mode is `onChange` so this fires as the operator
 *     types past the cap).
 *   - "Save brand brief" is independent from the theme save button — they
 *     PATCH different fields of the same Site row but the operator can
 *     iterate on one without touching the other.
 *   - "Generate with AI" opens the BrandBriefGeneratorDialog; on success
 *     the dialog calls `onApply(brandJson)` and we populate the form
 *     field-by-field with `shouldDirty: true` so the Save button enables.
 *
 * MUI only — no Tailwind / CSS modules / raw CSS per
 * `standards/react-patterns.md`.
 *
 * `data-testid` attributes for QA hooks:
 *   - `brand-brief-section` (root accordion)
 *   - `brand-brief-toggle` (the summary header)
 *   - `brand-brief-tagline-input`
 *   - `brand-brief-tone-input`
 *   - `brand-brief-audience-input`
 *   - `brand-brief-voice-notes-input`
 *   - `brand-brief-save-button`
 *   - `brand-brief-ai-button`
 */

import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Controller, useForm } from "react-hook-form";
import type { SiteResponse } from "@ottopilot/shared";
import { brandJsonV1, EMPTY_BRAND_JSON_V1 } from "@ottopilot/shared";
import type { BrandJsonV1 } from "@ottopilot/shared";
import { useUpdateSite } from "../../hooks/useSites";
import { useToast } from "../../components/ToastProvider";
import { makeZodResolver } from "../../lib/zodResolver";
import { BrandBriefGeneratorDialog } from "./BrandBriefGeneratorDialog";

/**
 * Parse the site's existing `brandJson` into a v1 shape the form can edit.
 * The API returns `brandJson: unknown` at the transport boundary because
 * the persisted column is `Json?` — clients narrow on their own. On parse
 * failure (or absent column for pre-#193 rows) we fall back to the empty
 * envelope so the editor is always usable; the operator's save will
 * overwrite the bad shape with a clean v1 brief.
 */
function parseBrandOrEmpty(raw: unknown): BrandJsonV1 {
  const parsed = brandJsonV1.safeParse(raw);
  return parsed.success ? parsed.data : { ...EMPTY_BRAND_JSON_V1 };
}

/**
 * Does the brand brief carry any operator-supplied content? A pristine
 * envelope from a new site shouldn't keep the accordion collapsed — the
 * operator hasn't seen the section yet.
 */
function isEmptyBrand(brand: BrandJsonV1): boolean {
  return (
    brand.tagline.trim().length === 0 &&
    brand.tone.trim().length === 0 &&
    brand.audience.trim().length === 0 &&
    brand.voiceNotes.trim().length === 0
  );
}

type Props = {
  site: SiteResponse;
};

export function BrandBriefSection({ site }: Props): ReactElement {
  const toast = useToast();
  const updateSite = useUpdateSite(site.id);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  // Memoize so RHF's default value reference is stable per
  // `site.brandJson` — otherwise every render produces a new object and
  // `isDirty` resets.
  const defaults = useMemo(() => parseBrandOrEmpty(site.brandJson), [site.brandJson]);

  // Default the accordion to expanded when the brief is empty so new
  // sites see the form on first visit; collapsed once the operator has
  // saved a brief so the theme editor takes default focus.
  const [expanded, setExpanded] = useState<boolean>(() => isEmptyBrand(defaults));

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<BrandJsonV1>({
    resolver: makeZodResolver(brandJsonV1),
    defaultValues: defaults,
    mode: "onChange",
  });

  // If the underlying site refetches (e.g. after a successful save), reset
  // the form to the new authoritative values so `isDirty` flips back to
  // false. Without this the Save button would stay enabled after a save.
  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  /**
   * Reusable `setValue` options for the AI-apply path — every field flips
   * `isDirty`, runs validation, and marks the control as touched so the
   * Save button enables and any per-field error surfaces immediately.
   */
  const applyOpts = { shouldDirty: true, shouldValidate: true, shouldTouch: true } as const;

  const onSubmit = handleSubmit((values) => {
    updateSite.mutate(
      { brandJson: values },
      {
        onSuccess: () => {
          toast.showSuccess("Brand brief saved.");
          reset(values);
        },
        onError: (err) => {
          toast.showError(err.message || "Could not save brand brief.");
        },
      },
    );
  });

  // Live values for per-field character counters. We watch the whole form
  // (it's only four short fields, no performance concern) so the counter
  // updates on every keystroke without per-field `watch` calls.
  const live = watch();

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, next) => setExpanded(next)}
      data-testid="brand-brief-section"
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="brand-brief-content"
        id="brand-brief-header"
        data-testid="brand-brief-toggle"
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Brand brief</Typography>
          <Typography variant="body2" color="text.secondary">
            Tagline, tone, audience, and voice notes — used by the AI generators.
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails id="brand-brief-content">
        {/*
          We deliberately do NOT render this section's controls inside a
          `<form>` element — the BrandBriefSection lives inside the
          ThemeTab's outer `<form>` and nesting forms is invalid HTML +
          breaks submit-bubbling. The Save button below carries the click
          handler directly and `type="button"` is intentional so a stray
          Enter key in any of the text fields does NOT submit the
          theme form above. The handler still routes through
          react-hook-form's `handleSubmit`, so client-side validation is
          identical to a real form submit.
        */}
        <Stack spacing={3}>
          {updateSite.isError ? (
            <Alert severity="error" data-testid="brand-brief-error-alert">
              {updateSite.error.message}
            </Alert>
          ) : null}

          {/*
            AI affordance — opens the brief dialog. Lives at the top of the
            section so it's visible regardless of where the operator's
            attention is in the form below.
          */}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={() => setAiDialogOpen(true)}
              data-testid="brand-brief-ai-button"
            >
              Generate with AI
            </Button>
          </Stack>

          <Controller
            name="tagline"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Tagline"
                fullWidth
                error={Boolean(errors.tagline)}
                helperText={
                  errors.tagline?.message ??
                  `One-line product positioning. ${String(live.tagline?.length ?? 0)} / 140`
                }
                inputProps={{
                  maxLength: 140,
                  "data-testid": "brand-brief-tagline-input",
                }}
              />
            )}
          />

          <Controller
            name="tone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Tone"
                fullWidth
                error={Boolean(errors.tone)}
                helperText={
                  errors.tone?.message ??
                  `e.g. "warm, expert, concise". ${String(live.tone?.length ?? 0)} / 80`
                }
                inputProps={{
                  maxLength: 80,
                  "data-testid": "brand-brief-tone-input",
                }}
              />
            )}
          />

          <Controller
            name="audience"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Audience"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                error={Boolean(errors.audience)}
                helperText={
                  errors.audience?.message ??
                  `Who is the copy written for? ${String(live.audience?.length ?? 0)} / 200`
                }
                inputProps={{
                  maxLength: 200,
                  "data-testid": "brand-brief-audience-input",
                }}
              />
            )}
          />

          <Controller
            name="voiceNotes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voice notes"
                fullWidth
                multiline
                minRows={4}
                maxRows={10}
                error={Boolean(errors.voiceNotes)}
                helperText={
                  errors.voiceNotes?.message ??
                  `Dos and don'ts for content writers. ${String(live.voiceNotes?.length ?? 0)} / 1000`
                }
                inputProps={{
                  maxLength: 1000,
                  "data-testid": "brand-brief-voice-notes-input",
                }}
              />
            )}
          />

          {/*
            Brand intensity (#232). Three-way enum picker that steers how
            vibrant the AI's emitted layout / palette / copy gets. Lives
            below voiceNotes so the operator sees the free-text fields
            first and treats intensity as a final "dial" they tune at the
            end. Field is optional — unset means no directive is added to
            the prompt and the AI Studio post-validator applies no
            intensity-driven clamps.
          */}
          <Controller
            name="intensity"
            control={control}
            render={({ field }) => (
              <Box data-testid="brand-brief-intensity-group">
                <Typography variant="subtitle2" gutterBottom>
                  Brand intensity
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={field.value ?? null}
                  onChange={(_e, next: BrandJsonV1["intensity"] | null) => {
                    // ToggleButtonGroup emits `null` when the operator
                    // re-clicks the active option to unselect. We translate
                    // null to `undefined` so the field round-trips through
                    // the optional Zod enum cleanly.
                    field.onChange(next ?? undefined);
                  }}
                  aria-label="Brand intensity"
                  size="small"
                  color="primary"
                >
                  <ToggleButton
                    value="tasteful"
                    aria-label="tasteful"
                    data-testid="brand-brief-intensity-tasteful"
                  >
                    Tasteful
                  </ToggleButton>
                  <ToggleButton
                    value="playful"
                    aria-label="playful"
                    data-testid="brand-brief-intensity-playful"
                  >
                    Playful
                  </ToggleButton>
                  <ToggleButton
                    value="bold"
                    aria-label="bold"
                    data-testid="brand-brief-intensity-bold"
                  >
                    Bold
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  Steers how vibrant the AI generators get. Optional.
                </Typography>
              </Box>
            )}
          />

          <Stack direction="row" justifyContent="flex-end">
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                void onSubmit();
              }}
              disabled={!isDirty || isSubmitting || updateSite.isPending}
              data-testid="brand-brief-save-button"
            >
              {updateSite.isPending ? "Saving..." : "Save brand brief"}
            </Button>
          </Stack>
        </Stack>

        <BrandBriefGeneratorDialog
          open={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          siteId={site.id}
          onApply={(brandJson) => {
            // Populate the form values with the AI proposal field-by-field
            // with `shouldDirty: true` so the Save button enables. We
            // deliberately do NOT use `reset(brandJson)` here — `reset`
            // sets the new values as the form's baseline, which would
            // leave `isDirty` false and keep Save disabled. The whole
            // point of the AI flow is "AI proposes, operator confirms",
            // and the confirm action is the Save button.
            setValue("tagline", brandJson.tagline, applyOpts);
            setValue("tone", brandJson.tone, applyOpts);
            setValue("audience", brandJson.audience, applyOpts);
            setValue("voiceNotes", brandJson.voiceNotes, applyOpts);
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
}
